import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    Dimmer,
    Form,
    Header,
    Icon,
    Label,
    Loader,
    Message,
    Segment,
    Table,
} from 'semantic-ui-react';

import { saveAs } from 'file-saver';
import { useAppContext } from '../../../Contexts/AppContext';
import Functions from '../../../Functions';
import Gen1TrunkingOrderHeader from '../../../Models/ViewModels/Gen1TrunkingOrderHeader';
import {
    getGen1StatusLabelMeta,
    GEN1_STATUS,
    isGen1Draft,
    isGen1Editable,
} from '../gen1Status';
import { getGen1ApplicationTypeLabel } from '../gen1ApplicationType';
const SYSTEM_TYPE_EXISTING = 'Existing System';

const USB_KEY_ROWS = [
    [
        'Grade1UnlimitedAccessUsbKeyQty',
        'KPGKEY01',
        'Grade-1 Unlimited Access USB Key',
    ],
    [
        'Grade2LimitedAccessUsbKeyQty',
        'KPGKEY02',
        'Grade-2 Limited Access USB Key',
    ],
    [
        'SystemManagerSoftwareWithGrade1KeyQty',
        'KPGKEYSM',
        'System Manager Software with Grade-1 Key',
    ],
    ['SystemManagerSoftwareQty', 'KPG-110SMK', 'System Manager Software'],
];

function buildUsbKeyItems(item) {
    if (!item?.isNeededUsbKeyAndSoftware) return [];
    const rows = [];
    for (const [field, code, label] of USB_KEY_ROWS) {
        const q = Number(item[field]) || 0;
        if (q > 0) rows.push({ Code: code, Item: label, Quantity: q });
    }
    return rows;
}

function pickOtherSystemId(serials, item) {
    const exQty = Number(item?.ExchangeRepeaterPairQty) || 0;
    if (exQty <= 0) return '';
    const row = serials.find(
        (s) => s.ExchangeSystemId && String(s.ExchangeSystemId).trim() !== ''
    );
    return row?.ExchangeSystemId ?? '';
}

/** Gen1TrunkingOrderHeader（ViewModel）を詳細表示用のプレーンオブジェクトに変換 */
function mapHeaderToDetailView(h) {
    if (!h) return null;
    const serials = h.Gen1TrunkingOrderRepeaterSerials ?? [];
    const firstOrderNum =
        serials
            .map((s) => s.OrderNumber)
            .find((n) => n != null && String(n).trim() !== '') ?? '';

    return {
        id: h.id,
        Status: getGen1StatusLabelMeta(h.Status).text,
        RequestedDate: h.RequestedDate ?? null,
        ConfirmedDate: h.ConfirmedDate ?? null,
        CompletedDate: h.CompletedDate ?? null,
        ContactName: h.ContactName,
        ContactEmail: h.ContactEmail,
        AddressLineOne: h.AddressLineOne,
        AddressLineTwo: h.AddressLineTwo,
        AddressLineThree: h.AddressLineThree,
        City: h.City,
        State: h.State,
        Zip: h.Zip,
        Country: h.Country,
        Telephone: h.Telephone,
        ManufacturersRep: h.ManufactureRep ?? '',
        OrderNumber: firstOrderNum || '-',
        DealerOrderNumber: h.DealerOrderNumber ?? h.dealerOrderNumber ?? '',
        SystemType: h.SystemType,
        SystemId: h.SystemId,
        IncludePurchasedRepeaters: h.IncludePurchasedRepeaters,
        Gen1TrunkingOrderItem: h.Gen1TrunkingOrderItem,
        OtherSystemId: pickOtherSystemId(serials, h.Gen1TrunkingOrderItem),
        RepeaterDetails: serials.map((s) => ({
            LineSequenceNumber: s.LineSequenceNumber,
            OperationType: s.OperationType,
            BaseModel: s.BaseModel,
            MarketCode: s.MarketCode,
            SerialNumber: s.SerialNumber,
            ExchangePairId: s.ExchangePairId,
        })),
        UsbKeyItems: buildUsbKeyItems(h.Gen1TrunkingOrderItem),
        LicenseKeyFiles: h.Gen1TrunkingOrderLicenseKeyFiles ?? [],
        AdditionalNotes: h.AdditionalNotes,
    };
}

export default function Gen1OrderDetail(props) {
    const {
        id,
        // Gen1.js から渡される。未指定時は Edit 非表示（安全側のデフォルト）。
        canUseGen1Application = false,
        onBackToList,
        onEditToApplication,
    } = props;
    const { Reducer, Actions } = useAppContext();
    const [actionMessage, setActionMessage] = useState(null);
    const [
        licenseKeyPartialDownloadWarning,
        setLicenseKeyPartialDownloadWarning,
    ] = useState(null);
    const [orderForm, setOrderForm] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailErrorMessage, setDetailErrorMessage] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [remindMailLoading, setRemindMailLoading] = useState(false);
    const [
        downloadActivationLaborRequestSheetLoading,
        setdownloadActivationLaborRequestSheetLoading,
    ] = useState(false);
    const [selectedLicenseKeyFileIds, setSelectedLicenseKeyFileIds] = useState(
        []
    );
    const [licenseKeyFilesDownloadLoading, setLicenseKeyFilesDownloadLoading] =
        useState(false);
    const licenseKeyFilesDownloadInFlightRef = useRef(false);

    const csrftoken = Reducer.csrftoken;

    const idNum = Number(id);
    const idInvalid =
        id == null || id === '' || Number.isNaN(idNum) || idNum < 1;

    useEffect(() => {
        if (!csrftoken || idInvalid) return undefined;
        let cancelled = false;
        setOrderForm(null);
        setSelectedLicenseKeyFileIds([]);
        setLicenseKeyPartialDownloadWarning(null);
        setDetailErrorMessage('');
        setDetailLoading(true);
        Actions.fetchGen1OrderById(csrftoken, id)
            .then((data) => {
                if (cancelled) return;
                setOrderForm(
                    new Gen1TrunkingOrderHeader({
                        ...(data?.Gen1TrunkingOrderHeader ?? data),
                        Gen1TrunkingOrderItem:
                            data?.Gen1TrunkingOrderItem ?? undefined,
                        Gen1TrunkingOrderRepeaterSerials:
                            data?.Gen1TrunkingOrderRepeaterSerials ?? undefined,
                        Gen1TrunkingOrderLicenseKeyFiles:
                            data?.Gen1TrunkingOrderLicenseKeyFiles ?? undefined,
                    })
                );
                setDetailLoading(false);
            })
            .catch((e) => {
                if (cancelled) return;
                setDetailErrorMessage(
                    e?.message ?? 'An error occurred while loading the order.'
                );
                setDetailLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, csrftoken, idInvalid]);

    const order = useMemo(() => mapHeaderToDetailView(orderForm), [orderForm]);

    const repeaterDetailRows = useMemo(() => {
        const details = order?.RepeaterDetails ?? [];
        const grouped = [];
        const normalRows = details
            .filter((row) => row?.ExchangePairId == null)
            .map((row, index) => ({
                key: row?.LineSequenceNumber ?? `row-${index}`,
                functionName: row?.OperationType ?? '-',
                model: row?.BaseModel ?? row?.Model ?? '-',
                marketCode: row?.MarketCode ?? '-',
                serialNumber: row?.SerialNumber ?? '-',
                exchangePairId: null,
            }));
        const exchangeRows = details
            .filter((row) => row?.ExchangePairId != null)
            .map((row, index) => ({
                key: row?.LineSequenceNumber ?? `row-ex-${index}`,
                functionName: row?.OperationType ?? '-',
                model: row?.BaseModel ?? row?.Model ?? '-',
                marketCode: row?.MarketCode ?? '-',
                serialNumber: row?.SerialNumber ?? '-',
                exchangePairId: row.ExchangePairId,
            }));
        grouped.push(...normalRows);
        const pairIds = [
            ...new Set(exchangeRows.map((x) => x.exchangePairId)),
        ].sort((a, b) => a - b);
        for (const pairId of pairIds) {
            grouped.push({
                key: `pair-${pairId}`,
                isPairHeader: true,
                pairNumber: pairId + 1,
            });
            grouped.push(
                ...exchangeRows.filter((x) => x.exchangePairId === pairId)
            );
        }
        return grouped;
    }, [order]);

    function handleEdit() {
        if (!csrftoken || !order) return;
        // 最新取得は Application 側で行う（ここでは注文IDを渡して遷移のみ）
        if (onEditToApplication) onEditToApplication(order.id);
    }
    function handleList() {
        if (onBackToList) onBackToList();
    }

    function handleDelete() {
        if (!csrftoken || !order) return;
        if (!isGen1Draft(order.Status)) return;

        const confirmed = window.confirm(
            `Delete this Draft order? (ID: ${order.id})`
        );
        if (!confirmed) return;

        setDeleteLoading(true);
        Actions.deleteGen1OrderById(csrftoken, order.id)
            .then(() => {
                setDeleteLoading(false);
                if (onBackToList) onBackToList();
            })
            .catch((e) => {
                setDeleteLoading(false);
                setActionMessage(
                    e?.message ?? 'An error occurred while deleting the order.'
                );
            });
    }

    function handleRemindMail() {
        if (!csrftoken || !order) return;
        const isOrdered = order.Status === GEN1_STATUS.ORDERED;
        if (!isOrdered) return;

        const confirmed = window.confirm(
            `Send a reminder email for this order? (ID: ${order.id})`
        );
        if (!confirmed) return;

        setRemindMailLoading(true);
        Actions.sendGen1OrderRemindMail(csrftoken, order.id)
            .then(() => {
                setRemindMailLoading(false);
                setActionMessage('Reminder email has been sent.');
            })
            .catch((e) => {
                setRemindMailLoading(false);
                setActionMessage(
                    e?.message ??
                        'An error occurred while sending the reminder email.'
                );
            });
    }

    function handleDownloadActivationLaborRequestSheet() {
        if (!csrftoken || !order) return;
        const isDraft = order.Status === GEN1_STATUS.DRAFT;
        if (isDraft) return;

        setdownloadActivationLaborRequestSheetLoading(true);
        Actions.downloadActivationLaborRequestSheet(csrftoken, order.id)
            .then((blob) => {
                setdownloadActivationLaborRequestSheetLoading(false);
                saveAs(blob, `activation_labor_request_sheet_${order.id}.pdf`);
            })
            .catch((e) => {
                setdownloadActivationLaborRequestSheetLoading(false);

                setActionMessage(
                    e?.message ??
                        'Failed to download the activation labor request sheet.'
                );
            });
    }

    const pageTitle = (
        <Header
            as="h2"
            icon="file alternate"
            content="Gen1 Trunking Order Form - Detail"
        />
    );

    const headerBarSimple = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            {pageTitle}
            <Button
                icon="arrow left"
                content="Back to List"
                onClick={onBackToList}
            />
        </div>
    );

    if (idInvalid) {
        return (
            <>
                {headerBarSimple}
                <Message warning>
                    Order not found. Please return to History and select an
                    order again. (ID: {id})
                </Message>
            </>
        );
    }

    if (!csrftoken) {
        return (
            <>
                {headerBarSimple}
                <Dimmer active inverted page>
                    <Loader>Loading session…</Loader>
                </Dimmer>
            </>
        );
    }

    if (detailErrorMessage) {
        return (
            <>
                {headerBarSimple}
                <Message error content={detailErrorMessage} />
            </>
        );
    }

    const showLoading =
        detailLoading ||
        (Boolean(id && csrftoken) && !detailErrorMessage && !order);

    if (showLoading) {
        return (
            <>
                {headerBarSimple}
                <Dimmer active inverted page>
                    <Loader>Loading order…</Loader>
                </Dimmer>
            </>
        );
    }

    if (!order) {
        return (
            <>
                {headerBarSimple}
                <Message warning>
                    Order not found. Please return to History and select an
                    order again. (ID: {id})
                </Message>
            </>
        );
    }

    const statusMeta = getGen1StatusLabelMeta(order.Status);
    const detailPageHeader = (
        <Header style={{ margin: 0 }}>
            <Icon name="file alternate" />
            <Header.Content>
                Gen1 Trunking Order Form - Detail
                <Label
                    size="large"
                    color={statusMeta.color || 'grey'}
                    icon={statusMeta.icon}
                    content={statusMeta.text || '-'}
                />
            </Header.Content>
        </Header>
    );
    // 編集・削除は Application メニュー権限が必要（History のみは参照専用）。
    const canEdit = canUseGen1Application && isGen1Editable(order.Status);
    const canDeleteDraft = canUseGen1Application && isGen1Draft(order.Status);
    const canDownloadActivationLaborRequestSheet =
        order.Status !== GEN1_STATUS.DRAFT;
    const canSendRemindMail = order.Status === GEN1_STATUS.ORDERED;
    const actionButtons = (
        <>
            {canEdit && (
                <Button icon="edit" content="Edit" onClick={handleEdit} />
            )}
            {canDownloadActivationLaborRequestSheet && (
                <Button
                    icon="download"
                    content="Download Activation Labor Request Sheet"
                    loading={downloadActivationLaborRequestSheetLoading}
                    disabled={downloadActivationLaborRequestSheetLoading}
                    onClick={handleDownloadActivationLaborRequestSheet}
                />
            )}
            {canSendRemindMail && (
                <Button
                    icon="mail"
                    content="Reminder email"
                    loading={remindMailLoading}
                    disabled={remindMailLoading}
                    onClick={handleRemindMail}
                />
            )}
            {canDeleteDraft && (
                <Button
                    icon="trash"
                    content="Delete"
                    negative
                    loading={deleteLoading}
                    disabled={deleteLoading}
                    onClick={handleDelete}
                />
            )}
            <Button
                icon="arrow left"
                content="Back to List"
                onClick={handleList}
                primary
            />
        </>
    );

    const isExistingSystem = order.SystemType === SYSTEM_TYPE_EXISTING;
    const applicationTypeLabel = getGen1ApplicationTypeLabel(
        order.IncludePurchasedRepeaters
    );
    const isCompletedStatus = order.Status === GEN1_STATUS.COMPLETED;

    const licenseKeyFiles = order.LicenseKeyFiles ?? [];
    const allLicenseKeyFileIds = licenseKeyFiles
        .map((f) => Number(f?.id))
        .filter((x) => Number.isFinite(x) && x > 0);
    const selectedIdSet = new Set(selectedLicenseKeyFileIds);
    const allSelected =
        allLicenseKeyFileIds.length > 0 &&
        allLicenseKeyFileIds.every((x) => selectedIdSet.has(x));
    const anySelected = selectedLicenseKeyFileIds.length > 0;

    function toggleLicenseKeyFile(idValue) {
        const idNum = Number(idValue);
        if (!Number.isFinite(idNum) || idNum < 1) return;
        setSelectedLicenseKeyFileIds((prev) => {
            const set = new Set(prev);
            if (set.has(idNum)) set.delete(idNum);
            else set.add(idNum);
            return Array.from(set);
        });
    }

    function handleSelectAllLicenseKeyFiles() {
        setSelectedLicenseKeyFileIds(allLicenseKeyFileIds);
    }
    function handleClearLicenseKeyFilesSelection() {
        setSelectedLicenseKeyFileIds([]);
    }

    function handleDownloadSelectedLicenseKeyFiles() {
        if (
            licenseKeyFilesDownloadInFlightRef.current ||
            licenseKeyFilesDownloadLoading ||
            !csrftoken ||
            !order?.id
        )
            return;
        const ids = selectedLicenseKeyFileIds;
        if (!Array.isArray(ids) || ids.length === 0) return;

        licenseKeyFilesDownloadInFlightRef.current = true;
        setLicenseKeyFilesDownloadLoading(true);
        setLicenseKeyPartialDownloadWarning(null);
        // 選択された License Key Files をサーバー側で ZIP にまとめて返す（Completed のみ）
        Actions.downloadGen1LicenseKeyFilesZip(csrftoken, order.id, ids)
            .then(({ blob, partialFailedLicenseKeyFileNames }) => {
                // 既存画面と同様に file-saver で保存する
                saveAs(blob, `Gen1_LicenseKeyFiles_${order.id}.zip`);
                const failed = partialFailedLicenseKeyFileNames ?? [];
                if (failed.length > 0) {
                    setLicenseKeyPartialDownloadWarning(
                        `The ZIP file was saved, but the following file(s) could not be downloaded: ${failed.join(', ')}.`
                    );
                } else {
                    setLicenseKeyPartialDownloadWarning(null);
                }
            })
            .catch((e) => {
                setLicenseKeyPartialDownloadWarning(null);
                setActionMessage(
                    e?.message ?? 'Failed to download license key files.'
                );
            })
            .finally(() => {
                licenseKeyFilesDownloadInFlightRef.current = false;
                setLicenseKeyFilesDownloadLoading(false);
            });
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {detailPageHeader}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    {actionButtons}
                </div>
            </div>
            {licenseKeyPartialDownloadWarning ? (
                <Message
                    warning
                    onDismiss={() => setLicenseKeyPartialDownloadWarning(null)}
                >
                    {licenseKeyPartialDownloadWarning}
                </Message>
            ) : null}
            {actionMessage && (
                <Message error onDismiss={() => setActionMessage(null)}>
                    {actionMessage}
                </Message>
            )}

            <Segment>
                <Header icon="info" content="Basic Information" dividing />
                <Form>
                    <Form.Group widths="equal">
                        <ReadOnlyField
                            label="Requested Date"
                            value={
                                Functions.dateForm(order.RequestedDate) || '-'
                            }
                        />
                        <ReadOnlyField
                            label="Confirmed Date"
                            value={
                                Functions.dateForm(order.ConfirmedDate) || '-'
                            }
                        />
                        <ReadOnlyField
                            label="Completed Date"
                            value={
                                Functions.dateForm(order.CompletedDate) || '-'
                            }
                        />
                    </Form.Group>
                </Form>
            </Segment>

            <Segment>
                <Header
                    icon="building"
                    content="Customer Information"
                    dividing
                />
                <Form>
                    <Form.Group widths="equal">
                        <ReadOnlyField
                            label="Contact Name"
                            value={order.ContactName || '-'}
                        />
                        <ReadOnlyField
                            label="Contact Email"
                            value={order.ContactEmail || '-'}
                        />
                    </Form.Group>
                    <ReadOnlyField
                        label="Address"
                        value={order.AddressLineOne || '-'}
                    />
                    <ReadOnlyField
                        hideLabel
                        value={order.AddressLineTwo || '-'}
                    />
                    <ReadOnlyField
                        hideLabel
                        value={order.AddressLineThree || '-'}
                    />
                    <Form.Group>
                        <ReadOnlyField
                            label="City"
                            value={order.City || '-'}
                            width={4}
                        />
                        <ReadOnlyField
                            label="State"
                            value={order.State || '-'}
                            width={4}
                        />
                        <ReadOnlyField
                            label="Zip/Postal Code"
                            value={order.Zip || '-'}
                            width={4}
                        />
                        <ReadOnlyField
                            label="Country"
                            value={order.Country || '-'}
                            width={4}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <ReadOnlyField
                            label="Telephone"
                            value={order.Telephone || '-'}
                        />
                        <ReadOnlyField
                            label="Manufacturers Rep"
                            value={order.ManufacturersRep || '-'}
                        />
                    </Form.Group>
                </Form>
            </Segment>

            <Segment>
                <Header
                    icon="wifi"
                    content="Gen1 Trunking Information"
                    dividing
                />
                <Form>
                    <Form.Group widths="equal">
                        {(order.DealerOrderNumber ?? '').trim() && (
                            <ReadOnlyField
                                label="Dealer Order Number"
                                value={order.DealerOrderNumber}
                            />
                        )}
                        <ReadOnlyField
                            label="System Type"
                            value={order.SystemType || '-'}
                        />
                        <ReadOnlyField
                            label="System ID"
                            value={order?.SystemId}
                        />
                        <ReadOnlyField
                            label="Application Type"
                            value={applicationTypeLabel}
                        />
                    </Form.Group>
                    {order?.IncludePurchasedRepeaters && (
                        <Form.Group widths="equal">
                            <ReadOnlyField
                                label="Sales Order Number (SO#)"
                                value={order.OrderNumber || '-'}
                            />
                        </Form.Group>
                    )}
                </Form>
            </Segment>

            <Segment>
                <Header icon="cogs" content="Repeater Operations" dividing />
                <Form>
                    <Form.Group>
                        <ReadOnlyField
                            label="Add Repeater"
                            value={toYesNo(
                                (order?.Gen1TrunkingOrderItem?.RepeaterQty ??
                                    0) > 0
                            )}
                            width={4}
                        />
                        <ReadOnlyField
                            label="KSGPG91800 Repeater Trunking Feature"
                            value={String(
                                order?.Gen1TrunkingOrderItem?.RepeaterQty ?? 0
                            )}
                            width={4}
                        />
                        <ReadOnlyField
                            label="KSGPG91810 Multi_site Networking Feature"
                            value={String(
                                order?.Gen1TrunkingOrderItem
                                    ?.MultiSiteNetworkingQty ?? 0
                            )}
                            width={4}
                        />
                    </Form.Group>
                    {isExistingSystem && (
                        <>
                            <Form.Group>
                                <ReadOnlyField
                                    label="Exchange Repeater"
                                    value={toYesNo(
                                        (order?.Gen1TrunkingOrderItem
                                            ?.ExchangeRepeaterPairQty ?? 0) > 0
                                    )}
                                    width={4}
                                />
                                <ReadOnlyField
                                    label="Exchange Repeater Quantity"
                                    value={String(
                                        order?.Gen1TrunkingOrderItem
                                            ?.ExchangeRepeaterPairQty ?? 0
                                    )}
                                    width={4}
                                />
                                <ReadOnlyField
                                    label="Other System ID"
                                    value={
                                        (order?.Gen1TrunkingOrderItem
                                            ?.ExchangeRepeaterPairQty ?? 0) > 0
                                            ? (order?.OtherSystemId ?? '-')
                                            : '-'
                                    }
                                    width={4}
                                />
                            </Form.Group>
                            <Form.Group>
                                <ReadOnlyField
                                    label="Remove Repeater"
                                    value={toYesNo(
                                        (order?.Gen1TrunkingOrderItem
                                            ?.RemoveRepeaterQty ?? 0) > 0
                                    )}
                                    width={4}
                                />
                                <ReadOnlyField
                                    label="Remove Repeater Quantity"
                                    value={String(
                                        order?.Gen1TrunkingOrderItem
                                            ?.RemoveRepeaterQty ?? 0
                                    )}
                                    width={4}
                                />
                                <ReadOnlyField label=" " value=" " width={4} />
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Segment>

            <Segment>
                <Header icon="list" content="Repeater Details" dividing />
                <Table striped celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Function</Table.HeaderCell>
                            <Table.HeaderCell>Model</Table.HeaderCell>
                            <Table.HeaderCell>Serial Number</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {repeaterDetailRows.length > 0 ? (
                            repeaterDetailRows.map((row) =>
                                row.isPairHeader ? (
                                    <Table.Row key={row.key}>
                                        <Table.Cell
                                            colSpan={4}
                                            style={{
                                                backgroundColor: '#f0f0f0',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Exchange Pair {row.pairNumber}
                                        </Table.Cell>
                                    </Table.Row>
                                ) : (
                                    <Table.Row key={row.key}>
                                        <Table.Cell>
                                            {row.functionName}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {[row.model, row.marketCode].every(
                                                (v) => v === '-'
                                            )
                                                ? '-'
                                                : [row.model, row.marketCode]
                                                      .filter(
                                                          (v) => v && v !== '-'
                                                      )
                                                      .join('')}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {row.serialNumber}
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            )
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan={4} textAlign="center">
                                    No repeater details.
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </Segment>

            <Segment>
                <Header icon="usb" content="USB Keys and Software" dividing />
                <Table striped celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Item</Table.HeaderCell>
                            <Table.HeaderCell>Quantity</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {order.UsbKeyItems && order.UsbKeyItems.length > 0 ? (
                            order.UsbKeyItems.map((row, i) => (
                                <Table.Row key={i}>
                                    <Table.Cell>{`${row.Code} - ${row.Item}`}</Table.Cell>
                                    <Table.Cell>{row.Quantity}</Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan={2} textAlign="center">
                                    No USB keys or software.
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </Segment>

            <Segment>
                <Header
                    icon="sticky note"
                    content="Additional Notes"
                    dividing
                />
                <Form>
                    <ReadOnlyField
                        hideLabel
                        value={order.AdditionalNotes || '-'}
                    />
                </Form>
            </Segment>

            {isCompletedStatus && (
                <Segment>
                    <Header icon="key" content="License Key Files" dividing />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                            marginBottom: 10,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: 8,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                type="button"
                                size="small"
                                content="Select All"
                                disabled={
                                    licenseKeyFiles.length === 0 ||
                                    allSelected ||
                                    licenseKeyFilesDownloadLoading
                                }
                                onClick={handleSelectAllLicenseKeyFiles}
                            />
                            <Button
                                type="button"
                                size="small"
                                content="Clear"
                                disabled={
                                    !anySelected ||
                                    licenseKeyFilesDownloadLoading
                                }
                                onClick={handleClearLicenseKeyFilesSelection}
                            />
                        </div>
                        <Button
                            type="button"
                            primary
                            size="small"
                            content="Download"
                            loading={licenseKeyFilesDownloadLoading}
                            disabled={
                                !anySelected || licenseKeyFilesDownloadLoading
                            }
                            onClick={handleDownloadSelectedLicenseKeyFiles}
                        />
                    </div>
                    <Table striped celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell collapsing>
                                    <Checkbox
                                        checked={allSelected}
                                        disabled={
                                            licenseKeyFiles.length === 0 ||
                                            licenseKeyFilesDownloadLoading
                                        }
                                        onChange={() =>
                                            allSelected
                                                ? handleClearLicenseKeyFilesSelection()
                                                : handleSelectAllLicenseKeyFiles()
                                        }
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>File Name</Table.HeaderCell>
                                <Table.HeaderCell>
                                    Uploaded Date
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {licenseKeyFiles.length > 0 ? (
                                licenseKeyFiles.map((f) => {
                                    const fileId = Number(f?.id);
                                    const fileName = f.FileName ?? '-';
                                    const uploadedDate = f.UploadedDate ?? null;
                                    return (
                                        <Table.Row key={fileId || fileName}>
                                            <Table.Cell collapsing>
                                                <Checkbox
                                                    checked={
                                                        Number.isFinite(
                                                            fileId
                                                        ) &&
                                                        fileId > 0 &&
                                                        selectedIdSet.has(
                                                            fileId
                                                        )
                                                    }
                                                    disabled={
                                                        !Number.isFinite(
                                                            fileId
                                                        ) ||
                                                        fileId < 1 ||
                                                        licenseKeyFilesDownloadLoading
                                                    }
                                                    onChange={() =>
                                                        toggleLicenseKeyFile(
                                                            fileId
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>{fileName}</Table.Cell>
                                            <Table.Cell>
                                                {uploadedDate
                                                    ? new Date(
                                                          uploadedDate
                                                      ).toLocaleDateString(
                                                          'en-US'
                                                      )
                                                    : '-'}
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={3} textAlign="center">
                                        No license key files available.
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Segment>
            )}

            <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
                {actionButtons}
            </div>
        </>
    );
}

function ReadOnlyField({ label, value, width, hideLabel = false }) {
    return (
        <Form.Field width={width} style={{ width: width ? undefined : '100%' }}>
            {!hideLabel && <label>{label}</label>}
            <div
                style={{
                    minHeight: 24,
                    padding: hideLabel ? '0 0 2px 0' : '2px 0',
                    color: 'rgba(0,0,0,0.87)',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {value}
            </div>
        </Form.Field>
    );
}

function toYesNo(value) {
    return value ? 'Yes' : 'No';
}
